<template>
    <div>
        <div class="tool-title">
          <div class="intro">
            <h1>Explore relationships between genes and biological functions learned from CRISPR fitness screens using Webster.</h1>
            <h2>Read the paper: <a href="https://arxiv.org/abs/2111.06247" class="header-link">“Sparse dictionary learning recovers pleiotropy from human cell fitness screens”</a> for more details.</h2>
            <article style="margin-top: 20px" class="message" :class="accordionClasses">
              <div class="message-header" @click="toggleAccordion">
                <span style="font-weight: 400; font-size: 20px; margin-right: 50px; color: #A9519D"
                :class="accordionIsOpen ? 'rotate-plus' : 'rotate-x'" >About this tool</span>
                <span style="color: #A9519D; float: left; font-size: 32px; font-weight: 600; margin-right: 10px; margin-top: -2px; margin-left: -8px; padding-bottom: 8px; display: inline-block" :class="{'rotate-plus': accordionIsOpen, 'rotate-x': true}" 
                  >+</span>
              </div>
              
              <div class="message-body">
                <div class="message-content">
                  <div class="flex-grid">
                  <div class="column" style="padding-left: 0px">
                  <img class="image" src="../assets/images/90degreegraphic.png" alt="">
                  <p>
                      Webster is an algorithm that infers gene functions from high-dimensional gene perturbation data. We applied it to datasets where genes are knocked out using CRISPR and the resulting changes in fitness over various cell contexts were measured.
                      In the below visualizations, each gene perturbation is represented as a gray data point on a 2D layout. Each function inferred by Webster is represented as a black data point on the same layout.
                  </p>
                  <p>
                    For each function, Webster returns a gene-to-function score, called a “loading”, that maps a subset of genes to an inferred function. The magnitude (and sign) of a loading reflects the contribution of that gene to that function.
                  </p>
                  <p>These loadings are represented in two complementary ways:</p>
                  <ol>
                    <li>as a colormap over data points</li>
                    <li>as a Z-axis when the “3D” mode is selected.</li>
                  </ol>
                  <h4 style="font-weight:600;margin-bottom: 10px">How to use</h4>
                  <ul>
                    <li><strong>Left sidebar:</strong> This sidebar is used to select the variable that is plotted on the z axis.</li>
                    <li><strong>Main window:</strong> The main window displays <span style="color: gray; font-weight: 500">genes</span> and <span style="color: black; font-weight: 500">functions</span> as data points.</li>
                    <li><strong>Right sidebar:</strong> This displays the annotation card for a gene or a function that is selected, either by (1) clicking a data point or (2) search bar in the main window.</li>
                  </ul>
                </div>
              </div>
                </div>
              </div>
              
            </article>
          </div>
        </div>
        <div class="flex-grid">
          <!-- umap controls -->
            <div class="column col-3 umap-controls">
                <div>
                  <select v-model="selectedDataset">
                      <option disabled value="">Select dataset</option>
                      <!-- NOTE: previously called "Durocher" -->
                      <option>Depmap</option>
                      <option value="Durocher">Genotoxic</option>
                  </select>

                  <!-- Depmap options -->
                  <select v-model="selectedFunctionGroup" v-if="selectedDataset == 'Depmap'">
                      <option disabled value="">Select function collection</option>
                      <option>All</option>
                      <option>Complex</option>
                      <option>Signaling</option>
                      <option>Subcellular</option>
                  </select>
                  <select v-model="selectedSubCollection" v-if="selectedDataset == 'Depmap' && selectedFunctionGroup != ''">
                      <option disabled value="">Select function subgroup</option>
                      <!-- TODO: sort alphabetical? -->
                      <option v-for="(d,i) in depmapCollectionList" :key="i">{{d}}</option>
                  </select>

                  <!-- Durocher options -->
                  <select v-model="selectedFunctionGroup" v-if="selectedDataset == 'Durocher'">
                      <option disabled value="">Select function collection</option>
                      <option>Genotoxic stress</option>
                  </select>
                  <select v-model="selectedSubCollection" v-if="selectedDataset == 'Durocher' && selectedFunctionGroup != ''">
                      <option disabled value="" >Select function group</option>
                      <option>DNA Damage response</option>
                      <option>Specific resistance or sensitivity</option>
                      <option>Common fitness</option>
                  </select>

                  <!-- Small multiples section -->
                  <div>
                    <div v-if="selectedSubCollection != ''">
                      <div v-if="selectedDataset == 'Depmap'">
                      <p class="small-multiple-nav">Click on function to highlight in main plot</p>
                      <button @click="assignZAxisToUmap(null, null)" class="clear-selection btn">clear function</button>
                      <h4>Links to raw function data:</h4>
                        <div style="font-size: 14px">
                        <a href="https://figshare.com/articles/dataset/Webster_Supplemental_Output/14963561?file=29073855">Loadings</a>
                        <br>
                        <a href="https://figshare.com/articles/dataset/Webster_Supplemental_Output/14963561?file=29073795">Biomarkers</a>
                        <br>
                        <a href="https://figshare.com/articles/dataset/Webster_Supplemental_Output/14963561?file=29073786">Geneset Enrichment</a>
                        <br>
                        <a href="https://figshare.com/articles/dataset/Webster_Supplemental_Output/14963561?file=29148159">All</a>
                        </div>
                      </div>
                    </div>
                    <hr>

                    <div v-if="durocherCollection != null  && selectedDataset == 'Durocher'">
                      <div v-for="(item, i) in durocherFunctionsForCards" :key="item.id">
                        <div 
                        @click="assignZAxisToUmap(item.name, i)" 
                        class="plot-card-container">
                          <p class="function-name plot-card-txt">{{lookupFunctionName('Durocher', item.name)}} ({{item.name}})</p>
                          <div :id="`durocher-`+item.name"
                          class="plot-card-img" :class="{'activeCard': i == activeIndex}">
                            <img 
                            :id="`image-`+i"
                            :src="item.image" 
                            :alt="lookupFunctionName('Durocher', item.name)">
                          </div>
                        </div>
                      </div>
                    </div>

                    <div v-else-if="depmapCollection != null  && selectedDataset == 'Depmap'" class="plot-card-container">
                      <div v-for="(item, i) in depmapFunctionsForCards" :key="item.id">
                        <div 
                        @click="assignZAxisToUmap(item.name, i)" 
                        class="plot-card-container">
                        <p class="function-name plot-card-txt">{{lookupFunctionName('Depmap', item.name)}} ({{item.name}})</p>
                        <div :id="`depmap-`+item.name"
                        class="plot-card-img" :class="{'activeCard': i == activeIndex}">
                          <img 
                          :id="`image-`+i"
                          :src="item.image" 
                          :alt="lookupFunctionName('Depmap', item.name)">
                        </div>
                        </div>
                      </div>
                    </div>
                    </div>
                </div>
            </div>

            <!-- Center card to hold UMAP -->
            <div class="column col-9 center-card">
                <div id="umap-controls" class="flex-grid">
                  <!-- Custom Vue multiselect component -->
                  <div class="column col-6" v-if="durocherSearchOptions != null">
                    <multiselect v-model="searchValue" 
                    :options="searchOptions" 
                    placeholder="Search to select a gene or function" 
                    label="Name" 
                    track-by="Name"></multiselect>
                  </div>
                  <!-- UMAP nav controls -->
                  <div class="column col-6">
                    <div class="btn-group umap-view" style="margin-right: 20px">
                      <button @click="switchView('umap')" :class="{active: umapActive}">2d</button>
                      <div v-if="selectedFunctionForUmap != null" style="display: inline">
                        <button @click="switchView('manhattan')" :class="{active: manhattanActive}">3d</button>
                      </div>
                      <div v-else style="display: inline" class="tooltip">
                        <button id="deactivated-btn">3d</button>
                        <span class="tooltiptext">Select a function in the side panel to flip views.</span>
                      </div>
                    </div>
                    <div class="nav-btn-group">
                      <button class="zoom-icons" @click="zoomUmap(1)"><img src="../assets/images/zoom_in.svg" width=15 height=15 alt="zoom in icon"></button>
                      <button class="zoom-icons" @click="zoomUmap(-1)"><img src="../assets/images/zoom_out.svg" width=15 height=15 alt="zoom out icon"></button>
                      <button class="btn" @click="switchView('reset')">reset view</button>
                      <button class="btn" @click="removeSelectFromSearch()">clear selection</button>
                    </div>
                  </div>
                </div>

                <Umap 
                v-if="renderComponent"
                :is3d="flatScatter" 
                :isOrbiting="orbiting"
                :umapDataset="selectedDataset" 
                :flatView="flatView" 
                :interaction="interactionType" 
                :zVar="selectedFunctionForUmap"
                :zoomLevel="zoomTracker"
                :highlightThisFromSearch="searchValue"
                :selectedFunctionFromSearch="searchFunction" />

                <!-- Legend below the UMAP -->
                <div style="height: 40px; margin-top: 10px;">
                  <div class="legend-item">
                      <span class="circle" style="background-color: rgba(22, 22, 22, 1)"></span><span>Functions</span>
                  </div>
                  <div class="legend-item">
                      <span class="circle" style="background-color: rgba(194, 194, 194, 0.8)"></span><span>Genes</span>
                  </div>
                  <div v-if="selectedFunctionForUmap != null" style="display: inline">
                    <div class="legend-item">
                        <span class="circle" style="background-color: #008cb7"></span><span>Gene positive association</span>
                    </div>
                    <div class="legend-item">
                        <span class="circle" style="background-color: #ef2c0c"></span><span>Gene negative association</span>
                    </div>
                  </div>
                </div>
                <div style="margin-left: 8px;">
                  Native mouse controls: <i style="margin-left: 12px;" class="arrow left"></i><i class="arrow right"></i> = pan right left <i style="margin-left: 12px;" class="arrow up"></i><i style="margin-left: 6px;" class="arrow down"></i> = zoom
                </div>

                <div id="table-container" v-if="scoresForTable != null">
                  <table>
                    <tr>
                      <th class="sortable-header" @click="sortTableBy('gene')">
                        Gene 
                        <span v-if="tableSortedDescendingName">▲</span>
                        <span v-else @click="sortTableBy('gene')">▼</span>
                      </th>
                      <th class="sortable-header" v-if="selectedDataset == 'Durocher'" style="text-align: right">
                        Loading score for {{lookupFunctionName('Durocher', searchFunction.name)}}
                        <span v-if="tableSortedDescendingValue" @click="sortTableBy('values')">▲</span>
                        <span v-else>▼</span>
                        </th>
                      <th class="sortable-header" v-else style="text-align: right" @click="sortTableBy('values')">
                        Loading score for {{lookupFunctionName('Depmap', searchFunction.name)}} 
                        <span v-if="tableSortedDescendingValue" @click="sortTableBy('values')">▲</span>
                        <span v-else>▼</span>
                      </th>
                      <th>View on Depmap portal</th>
                    </tr>
                    <tr v-for="(d,i) in scoresForTable" :key="i">
                      <td width=200>{{d.name}}</td>
                      <td style="text-align: right">{{roundDecimal(d.value)}}</td>
                      <td><a target="_blank" :href="`https://dmc.depmap.org/portal/gene/`+trimGeneName(d.name)+`?tab=overview`">Go to gene</a></td>
                    </tr>
                  </table>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import Umap from '../components/Umap.vue'
import Multiselect from 'vue-multiselect'
import {tsv} from 'd3';

export default {
    name: "ExplorerPage",
    components: {
        Umap,
        Multiselect
    },
    data() {
        return {
          accordionIsOpen: false,
          flatScatter: true,
          orbiting: false,
          flatView: true,
          zoomTracker: {},
          zoomIterator: 0,
          interactionType: "pan",
          selectedDataset: 'Depmap',
          selectedFunctionForUmap: null,
          selectedSubCollection: '',
          selectedFunctionGroup: '',
          highlightedGeneSet: '',
          depmapCollectionList: '',
          umapActive: true,
          manhattanActive: false,
          searchValue: null,
          searchFunction: null,
          durocherSearchOptions: null,
          depmapSearchOptions: null,
          searchOptions: null,
          durocherCollection: null,
          depmapCollection: null,
          durocherFunctionsForCards: null,
          depmapFunctionsForCards: null,
          activeIndex: null,
          durocherFunctionDisplay: null,
          depmapFunctionDisplay: null,
          durocherGeneToFunction: null,
          depmapGeneToFunction: null,
          renderComponent: true,
          scoresForTable: null,
          tableSortedDescendingName: false,
          tableSortedDescendingValue: true
          }
  },
  watch: {
    searchValue(val) {
      // console.log(val)
      if (val != null) {
        if (this.selectedDataset == "Durocher") {
          this.selectedFunctionGroup = "Genotoxic stress";
          if (val.Type == "Gene") {
              // find the object for this gene
              let zScorePrimitive = this.durocherGeneToFunction.find(d => d.Name == val.Name);

              let zScores = zScorePrimitive;

              // remove the name of the gene
              delete zScores.Name;

              // convert to an arr so we can sort
              var sortable = [];
              for (var score in zScores) {
                  sortable.push([score, zScores[score]]);
              }

              // sort by value
              sortable.sort(function(b, a) {
                  return a[1] - b[1];
              });

              // store the top value (positive) in a new obj with the function name for later lookup
              const topScore = {};
              topScore.functionNumber = sortable[0][0];
              topScore.value = sortable[0][1];

              let foundFunction = this.durocherCollection.find(d => d.Function == topScore.functionNumber);

              this.selectedSubCollection = foundFunction["Sub-collection"];

              // wait for list of images to render
              setTimeout(() => {
                // identify the index of this list of images
                let index = document.getElementById("durocher-"+topScore.functionNumber).childNodes[0].id.split("-")[1];
                
                // assign z axis
                this.assignZAxisToUmap(topScore.functionNumber, index)

              }, 200)
          } else {
              // split so we only store the v#
              let functionNumber = val.Name.split("(")[1];
              let functionNumberClean = functionNumber.split(")")[0];
              
              let foundFunction = this.durocherCollection.find(d => d.Function == functionNumberClean);

              this.selectedSubCollection = foundFunction["Sub-collection"];

              // highlight the chosen function card
              setTimeout(() => {
                // identify the index of this list of images
                let index = document.getElementById("durocher-"+functionNumberClean).childNodes[0].id.split("-")[1];

                this.assignZAxisToUmap(functionNumberClean, index);
              }, 200)
          }
        } else {
          
          if (val.Type == "Gene") {
            // find the object for this gene
              let zScores = this.depmapGeneToFunction.find(d => d.Name == val.Name);
              
              // remove the name of the gene
              delete zScores.Name;

              // convert to an arr so we can sort
              var sortable = [];
              for (var score in zScores) {
                  sortable.push([score, zScores[score]]);
              }

              // sort by value
              sortable.sort(function(b, a) {
                  return a[1] - b[1];
              });

              // store the top value (positive) in a new obj with the function name for later lookup
              const topScore = {};
              topScore.functionNumber = sortable[0][0];
              topScore.value = sortable[0][1];

              let foundFunction = this.depmapCollection.find(d => d.Name == topScore.functionNumber);
              
              this.selectedFunctionGroup = foundFunction["Collection"];
              this.selectedSubCollection = foundFunction["Subcollection"];

              // wait for list of images to render
              setTimeout(() => {
                // identify the index of this list of images
                let index = document.getElementById("depmap-"+topScore.functionNumber).childNodes[0].id.split("-")[1];
                
                // assign z axis
                this.assignZAxisToUmap(topScore.functionNumber, index)
              }, 100)
          } else {
            this.selectedFunctionGroup = "All";

            // split so we only store the v#
              let lastFive = val.Name.substr(val.Name.length - 6); // => "(V189)"
              let functionNumber = lastFive.split("(")[1];
              let functionNumberClean = functionNumber.split(")")[0];
              
              let foundFunction = this.depmapCollection.find(d => d.Name == functionNumberClean);

              this.selectedSubCollection = foundFunction["Subcollection"];

              // wait for list of images to render
              setTimeout(() => {
                // identify the index of this list of images
                let index = document.getElementById("depmap-"+functionNumberClean).childNodes[0].id.split("-")[1];
                
                // assign z axis
                this.assignZAxisToUmap(functionNumberClean, index)
              }, 100)
          }
        }
      }
      
    },
    selectedFunctionGroup(val) {
      function isPartOfCollection(d) {
        return d.Collection == val
      }

      let filteredCollection = this.depmapCollection.filter(isPartOfCollection);

      // store the unique sub-collections in list for dropdown
      this.depmapCollectionList = [...new Set(filteredCollection.map(entry => entry["Subcollection"]))]
    },
    selectedDataset(val) {
      this.selectedFunctionForUmap = null;

      if (val == "Durocher") {
        this.searchOptions = this.durocherSearchOptions
      } else {
        this.searchOptions = this.depmapSearchOptions
      }

      this.selectedSubCollection = '';

      this.removeSelectFromSearch();

      this.forceRerender();

      this.switchView('umap')
    },
    selectedSubCollection(val) {
      if (this.selectedDataset == "Durocher") {
        this.getDurocherFunctions(val)
      } else {
        this.getDepmapFunctions(val)
      }
      this.activeIndex = null
    }
  },
  computed: {
    accordionClasses: function() {
          return {
            'is-closed': !this.accordionIsOpen,
            'is-primary': this.accordionIsOpen,
            'is-dark': !this.accordionIsOpen
          };
        }
  },
  mounted() {
    let durocherNames = [];
    let depmapNames = [];

    Promise.all([
      tsv("https://broadinstitute.github.io/webster-umap-minisite/data/durocher_umap.tsv", function(d) {
        return {
          Name: d.Name,
          X: +d.X,
          Y: +d.Y,
          Type: d.Type
        }
      }),
      tsv("https://broadinstitute.github.io/webster-umap-minisite/data/depmap_umap.tsv", function(d) {
        return {
          Name: d.Name,
          X: +d.X,
          Y: +d.Y,
          Type: d.Type
        }
      }),
      tsv("https://broadinstitute.github.io/webster-umap-minisite/data/durocher_collections.tsv", function(d) {
        return d
      }),
      tsv("https://broadinstitute.github.io/webster-umap-minisite/data/depmap_collections.tsv", function(d) {
        return d
      }),
      tsv('https://broadinstitute.github.io/webster-umap-minisite/data/durocher_fn_display_name.tsv', function(d) {
        return d;
      }),
      tsv('https://broadinstitute.github.io/webster-umap-minisite/data/depmap_fn_manual_name.tsv', function(d) {
        return d;
      }),
      tsv('https://broadinstitute.github.io/webster-umap-minisite/data/durocher_gene_to_function_stdev.tsv', function(d) {
        return d;
      }),
      tsv('https://broadinstitute.github.io/webster-umap-minisite/data/depmap_gene_to_function_stdev.tsv', function(d) {
        return d;
      })
    ]).then((dataset) => { 
      this.durocherGeneToFunction = dataset[6];
      this.depmapGeneToFunction = dataset[7];

      // store collection names for small multiple
      this.durocherCollection = dataset[2];
      this.depmapCollection = dataset[3];

      // for finding function display names
      this.durocherFunctionDisplay = dataset[4];
      this.depmapFunctionDisplay = dataset[5];

      // for both datasets, pull out all unique Names for search terms
      dataset[0].forEach((d,i) => {
        let displayName;
        if (d.Type == "Function") {
          displayName = this.lookupFunctionName("Durocher", d.Name);
          displayName = displayName + " (" + d.Name + ")";
        } else {
          displayName = d.Name
        }
        durocherNames.push({
          Name: displayName,
          Type: d.Type,
          index: i
        })
      }),
      dataset[1].forEach((d,i) => {
        let displayName;
        if (d.Type == "Function") {
          displayName = this.lookupFunctionName("Depmap", d.Name);
          displayName = displayName + " (" + d.Name + ")";
        } else {
          displayName = d.Name
        }
        depmapNames.push({
          Name: displayName,
          Type: d.Type,
          index: i
        })
      })

      // store the unique sub-collections in list for dropdown
      this.depmapCollectionList = [...new Set(this.depmapCollection.map(entry => entry["Subcollection"]))]  
      
      // store search names
      this.durocherSearchOptions = durocherNames;
      this.depmapSearchOptions = depmapNames;

      // set search items to durocher by default
      this.searchOptions = this.depmapSearchOptions;
    })
  },
  methods: {
    sortTableBy(val) {
    
      if (val == "gene") {
        this.tableSortedDescendingName = !this.tableSortedDescendingName;

        if (this.tableSortedDescendingName) {
          this.scoresForTable.sort((a, b) => a.name.localeCompare(b.name))
        } else {
          this.scoresForTable.sort((a, b) => b.name.localeCompare(a.name))
        }
      } else if (val == "values") {
        this.tableSortedDescendingValue = !this.tableSortedDescendingValue;

        if (this.tableSortedDescendingValue) {
          this.scoresForTable.sort(function(a, b) {
            return parseFloat(b.value) - parseFloat(a.value);
        });
        } else {
          this.scoresForTable.sort(function(a, b) {
            return parseFloat(a.value) - parseFloat(b.value);
        });
        }
        
      } else {
        console.log('error when sorting table: probs a typo')
      }
    },
    trimGeneName(val) {
      let name = val.split('(');

      return name[0].split(' ')[0];
    },
    toggleAccordion: function() {
        this.accordionIsOpen = !this.accordionIsOpen;
      },
    roundDecimal(val) {
      if (val == undefined) {
        return ''
      } else {
        const num = (val/1).toFixed(2)
        return num.toString()
      }
    },
    forceRerender() {
      console.log('force rerender')
        // Remove my-component from the DOM
        this.renderComponent = false;

        this.$nextTick(() => {
          // Add the component back in
          this.renderComponent = true;
        });
      },
    lookupFunctionName(dataset, fn) {
      let displayName;
      if (fn == undefined) {
        displayName = ''
      } else {
        if (dataset == "Durocher") {
          let name = this.durocherFunctionDisplay.find(element => element.Name == fn);
          displayName = name.Display_Name;
        } else {
          let name = this.depmapFunctionDisplay.find(element => element["Name"] == fn);
          displayName = name["Manual Name"];
        }
      }

      return displayName
    },
     getDurocherFunctions(val) {
        const functions = []

        let imgUrl, entry;
        this.durocherCollection.forEach((d,i) => {
          if (d["Sub-collection"] == val) {
            imgUrl = require(`../assets/images/durocher_svg_`+d.Function+`.svg`);

            entry = {'name': d.Function, 'id': i, 'image': imgUrl};
            
            functions.push(entry)
          }
        })

      this.durocherFunctionsForCards = functions;
   },
   getDepmapFunctions(val) {
      const functions = []
      
      let imgUrl, entry;
      this.depmapCollection.forEach((d,i) => {
        
        
        if (d["Subcollection"] == val) {
          
          imgUrl = require(`../assets/images/depmap_svg_`+d.Name+`.svg`);

          entry = {'name': d.Name, 'id': i, 'image': imgUrl};
          
          functions.push(entry)
        }
      })

      this.depmapFunctionsForCards = functions;
   },
    switchView(view) {
      if (view == "umap") {
        this.umapActive = true;
        this.manhattanActive = false;
        this.flatView = true;
      } else if (view == "manhattan") {
        this.umapActive = false;
        this.manhattanActive = true;
        this.flatView = false;
      } else if (view == "reset") {
        console.log('reset')
        if (this.flatView) {
          this.flatView = false
          setTimeout(()=>{ 
              this.flatView = true
            }, 10);
        } else {
          this.flatView = true
          setTimeout(()=>{ 
              this.flatView = false
            }, 10);
        }
      }
    },
    removeSelectFromSearch() {
      // reset all values to null so they disappear from the DOM until new user input
      this.searchValue = null;
      this.searchFunction = null;
      this.scoresForTable = null;
    },
    assignZAxisToUmap(item, index) {
      this.selectedFunctionForUmap = item;
      this.activeIndex = index;

       function findNonZeroScores(dataset, selectedFunction, searchGene) {
        let arrOfObjs = [];
        dataset.forEach(d => {
            let obj = {};
            if (d.Name == undefined) {
              // if name is missing, this is a gene from the search bar and we need to re-insert it
              obj.name = searchGene;
            } else {
              obj.name = d.Name;
            }
            
            obj.value = d[selectedFunction];

            // only keep non-zero scores
            if (obj.value != "0") {
              arrOfObjs.push(obj)
            }
        })

        arrOfObjs.sort(function(a, b) {
            return parseFloat(b.value) - parseFloat(a.value);
        });

        return arrOfObjs;
      }
      
      let found;
      if (this.selectedDataset == "Durocher") {
        found = this.durocherFunctionsForCards.find(d => d.name == item)
        found.dataset = "Durocher";
        this.searchFunction = found;

        this.scoresForTable = findNonZeroScores(this.durocherGeneToFunction, this.selectedFunctionForUmap, this.searchValue.Name);
        
      } else {
        found = this.depmapFunctionsForCards.find(d => d.name == item)
        found.dataset = "Depmap";
        this.searchFunction = found;

        this.scoresForTable = findNonZeroScores(this.depmapGeneToFunction, this.selectedFunctionForUmap, this.searchValue.Name);
      }

      if (item == null) {
        this.switchView('umap')
      }
    },
    zoomUmap(val) {
      if (val == 1) {
        this.zoomIterator = this.zoomIterator+val;
        this.zoomTracker = {
          direction: "in",
          val: this.zoomIterator
        }
      } else {
        this.zoomIterator = this.zoomIterator-val;
        this.zoomTracker = {
          direction: "out",
          val: this.zoomIterator
        }
      }
    }
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="css" scoped>

.legend-item {
  display: inline;
  padding: 10px;
  margin-top: 5px;
  height: 50px
}

.activeCard {
  border: 1px solid black;
}

.circle {
  border-radius: 50%;
  display: inline-block;
  height: 10px;
  width: 10px;
  margin-right: 10px
}

.tooltip {
  position: relative;
  display: inline-block;
  border-bottom: 1px dotted black;
}

.tooltip .tooltiptext {
  visibility: hidden;
  width: 160px;
  background-color: rgb(255, 255, 255);
  border: 1px solid gray;
  border-radius: 10px;
  color: rgb(63, 63, 63);
  text-align: center;
  padding: 15px 10px;
  
  /* Position the tooltip */
  position: absolute;
  z-index: 1;
  bottom: 100%;
  left: 50%;
  margin-left: -80px;
}

.tooltip:hover .tooltiptext {
  visibility: visible;
}

.tool-title {
  margin: 10px;
  padding: 10px
}

.umap-controls {
  margin: 10px;
  margin-top: -10px;
  padding: 10px
}

#umap-controls >* {
  padding-left: 10px;
  margin-left: 10px;
}

.btn-group {
  display: inline-block;
  margin-bottom: -10px;
}

.nav-btn-group {
  display: inline-block;
  margin-top: -5px;
}

.zoom-icons {
  background-color: white;
  border: none;
}

.zoom-icons:hover {
  background-color: white;
  border: none;
}

.umap-view button {
  background-color: #f0f0f0; 
  border: 1px solid rgb(203, 203, 203);
  color: black; /* White text */
  padding: 10px 24px; /* Some padding */
  cursor: pointer; /* Pointer/hand icon */
  float: left; /* Float the buttons side by side */
}

.btn-group button:not(:last-child) {
  border-right: none; /* Prevent double borders */
}

/* Clear floats (clearfix hack) */
.btn-group:after {
  content: "";
  clear: both;
  display: table;
}

/* Add a background color on hover */
.btn-group button:hover {
  background-color: #bbbbbb;
}

.btn-group .active {
  background-color: #969696;
}

#deactivated-btn {
  display: inline;
  opacity: 0.5;
  border: 1px solid gray;
}

#deactivated-btn:hover {
  cursor: not-allowed;
}

#gene-search {
  width: 600px;
  background-color: #EBEBEB;
  color: gray;
  border-radius: 10px;
  border: none
}

select {
  padding: 4px;
  margin-top: 5px;
  margin-bottom: 5px;
  display: block;
  background-color: #EBEBEB;
  color: gray;
  border-radius: 10px;
  border: none
}

input {
  border: 1px solid gray;
  border-radius: 20px;
  padding: 5px;
  padding-left: 12px;
  max-width: 300px
}

.inline {
  display: inline
}

.flex-lg {
  flex-grow: 4
}

.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
}

.switch input { 
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  -webkit-transition: .4s;
  transition: .4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  -webkit-transition: .4s;
  transition: .4s;
}

input:checked + .slider {
  background-color: #2196F3;
}

input:focus + .slider {
  box-shadow: 0 0 1px #2196F3;
}

input:checked + .slider:before {
  -webkit-transform: translateX(26px);
  -ms-transform: translateX(26px);
  transform: translateX(26px);
}

/* Rounded sliders */
.slider.round {
  border-radius: 24px;
}

.slider.round:before {
  border-radius: 50%;
}

  .plot-card-container {
        display: flex;
        flex-wrap: wrap;
      }
    
  .plot-card-img {
      flex: 0 0 44%;
      width: 100%;
      height: auto;
      background: #FFFFFF 0% 0% no-repeat padding-box;
      box-shadow: 0px 0px 4px #00000029;
      margin: 5px
  }

  .plot-card-txt {
      flex: 0 0 42%;
      width: 200px;
  }

  .plot-card-img img {
    width: 140px;
    height: 140px;
    /* transform: rotate(-90deg); */
    /* -webkit-transform: scaleX(-1);
  transform: scaleX(-1); */
  }

  .small-multiple-nav {
    font-style: italic;
    font-size: 12px;
    display: inline
  }

  .clear-selection {
    float: right;
  }

  .center-card {
    background: #FFFFFF 0% 0% no-repeat padding-box;
    box-shadow: 0px 0px 4px #00000029;
  }

  .function-name {
    font-size: 18px;
    padding: 5px;
    margin-left: 5px;
    /* overflow-wrap: break-word; */
  }

  .arrow {
  border: solid black;
  border-width: 0 3px 3px 0;
  display: inline-block;
  padding: 3px;
}

.right {
  transform: rotate(-45deg);
  -webkit-transform: rotate(-45deg);
}

.left {
  transform: rotate(135deg);
  -webkit-transform: rotate(135deg);
}

.up {
  transform: rotate(-135deg);
  -webkit-transform: rotate(-135deg);
}

.down {
  transform: rotate(45deg);
  -webkit-transform: rotate(45deg);
}

.message {
  margin-left: auto;
  margin-right: auto;
}
.message-header {
  cursor: pointer;
  padding: 10px;
}
.message-body   {
  padding: 0px;
  overflow: hidden;
  transition: 0.8s ease all;
}

.message-body p, li, ol {
  font-size: 20px;
}

.message-body h4 {
  font-size: 24px;
}
.is-closed .message-body {
  max-height: 0;
}
.message-content {
  padding: 20px;
}

.rotate-x {
  transform: rotate(0);
	transition:1s;
		
}

.rotate-plus {
  transform-origin: center;
  transform: rotate(45deg);
  -webkit-transform: rotate(45deg);
	-o-transition:1s;
	-ms-transition:1s;
	-moz-transition:1s;
	-webkit-transition:1s;
	transition:1s;
	   filter: progid:DXImageTransform.Microsoft.BasicImage(rotation=2);
}

#table-container {
  padding-left: 50px;
  padding-right: 50px;
  padding-top: 10px;
}

table {
  padding: 20px;
  border-collapse: collapse;
  width: 90%;
}
#table-container td, th {
  font-size: 14px;
  border: 1px solid #f4f4f4;
  text-align: left;
  padding-left: 8px;
  padding-right: 8px
}

#table-container tr:nth-child(even) {
  background-color: #f4f4f4;
}

.sortable-header {
  cursor: pointer;
}

.intro  {
  max-width: 1100px;
  margin: 0 auto;
}

.intro h1 {
  font-size: 32px
}

.intro h1,h2 {
  font-weight: 300;
}

.header-link {
  border-bottom: solid 2px #A9519D;
}
</style>